/**
 * The menu bar, as data: File, Edit, View, Library, Playback, Tools, Plugins,
 * Help -- the standard desktop order, so things are where people look for them.
 *
 * Built from the app's current state (so a check mark is always true) and then
 * merged with whatever plugins registered for each menu. The same list feeds
 * the menu bar, the compact layout's ☰ menu, and the shortcut table, so a
 * shortcut printed in a menu always works.
 */

import { useMemo } from "react";
import { useApp } from "../core/app";
import { addFolder, importFlowFile, newPlaylist, rescanLibrary } from "../core/commands";
import { player, usePlayer } from "../core/player";
import { useRegistry, type MenuId, type MenuItem } from "../core/registry";
import { useJobs } from "../core/useJobs";
import { signOut, useSession } from "../core/session";
import { checkAndTell } from "../core/updates";
import { isDesktop } from "../platform";
import { EMPTY_FILTERS } from "../search/filters";

export interface Menu {
  id: MenuId;
  label: string;
  items: MenuItem[];
}

export const MOD = typeof navigator !== "undefined" && /Mac/.test(navigator.platform) ? "⌘" : "Ctrl";

export function useMenus(): Menu[] {
  const app = useApp();
  const p = usePlayer();
  const reg = useRegistry();
  const jobs = useJobs(app.offline);
  const offline = app.offline;
  const offlineHint = "The engine is not running";
  const session = useSession();

  return useMemo(() => {
    const layouts = reg.layouts;
    const menus: Menu[] = [
      {
        id: "file",
        label: "File",
        items: [
          { id: "add-folder", group: "a", permission: "library.folders", label: "Add music folder…", icon: "folder", shortcut: `${MOD}+O`, disabled: offline, hint: offline ? offlineHint : undefined, run: () => addFolder(app) },
          { id: "new-playlist", group: "b", permission: "playlists.edit", label: "New playlist…", icon: "list", shortcut: `${MOD}+N`, disabled: offline, run: () => newPlaylist(app) },
          { id: "new-flow", group: "b", permission: "flows.edit", label: "New flow", icon: "wave", run: () => app.navigate("flow", { new: "1" }) },
          { id: "import-flow", group: "b", permission: "flows.edit", label: "Open .novaflow file…", icon: "upload", disabled: offline, run: () => importFlowFile(app) },
          { id: "settings", group: "c", label: "Settings…", icon: "settings", shortcut: `${MOD}+,`, run: () => app.openSettings("general") },
          ...(session.enabled && session.user
            ? [{ id: "sign-out", group: "d", label: `Sign out (${session.user.displayName})`, icon: "user" as const, hint: session.user.role, run: () => void signOut() }]
            : []),
        ],
      },
      {
        id: "edit",
        label: "Edit",
        items: [
          { id: "find", group: "a", label: "Find…", icon: "search", shortcut: `${MOD}+K`, run: () => focusSearch() },
          { id: "clear-search", group: "a", label: "Clear search and filters", disabled: !app.input && !app.query.clauses.length, run: () => { app.setInput(""); app.setFilters(EMPTY_FILTERS); } },
          { id: "tags", group: "b", label: "Edit tags…", icon: "edit", shortcut: `${MOD}+I`, disabled: !app.focused, hint: app.focused ? undefined : "Select a song first", run: () => { if (app.focused) app.navigate("studio", { id: app.focused }); } },
        ],
      },
      {
        id: "view",
        label: "View",
        items: [
          ...(app.layoutLocked ? [] : layouts).map((l, i) => ({ id: `layout-${l.id}`, group: "a", order: i, label: `${l.label} layout`, checked: app.layout === l.id, shortcut: i < 9 ? `${MOD}+${i + 1}` : undefined, hint: l.description, run: () => app.setLayout(l.id) })),
          { id: "theme", group: "b", label: "Theme", icon: app.theme.resolved === "dark" ? "moon" : "sun", submenu: [
            { id: "light", label: "Light", checked: app.theme.choice === "light", run: () => app.theme.setChoice("light") },
            { id: "dark", label: "Dark", checked: app.theme.choice === "dark", run: () => app.theme.setChoice("dark") },
            { id: "system", label: "Follow system", checked: app.theme.choice === "system", run: () => app.theme.setChoice("system") },
          ] },
          { id: "notation", group: "b", label: "Key notation", icon: "key", submenu: [
            { id: "camelot", label: "Camelot (8A)", checked: app.notation === "camelot", run: () => app.setNotation("camelot") },
            { id: "musical", label: "Note names (A minor)", checked: app.notation === "musical", run: () => app.setNotation("musical") },
          ] },
          { id: "sidebar", group: "c", label: "Sidebar", checked: app.sidebar, shortcut: `${MOD}+B`, run: () => app.setSidebar(!app.sidebar) },
          { id: "inspector", group: "c", label: "Details panel", checked: app.inspector, shortcut: `${MOD}+J`, run: () => app.setInspector(!app.inspector) },
        ],
      },
      {
        id: "library",
        label: "Library",
        items: [
          { id: "songs", group: "a", label: "Songs", icon: "music", run: () => app.navigate("songs") },
          { id: "albums", group: "a", label: "Albums", icon: "disc", run: () => app.navigate("albums") },
          { id: "artists", group: "a", label: "Artists", icon: "user", run: () => app.navigate("artists") },
          { id: "genres", group: "a", label: "Genres", icon: "tag", run: () => app.navigate("genres") },
          { id: "folders", group: "a", label: "Folders", icon: "folder", run: () => app.navigate("folders") },
          { id: "add-folder", group: "b", label: "Add music folder…", icon: "folder", shortcut: `${MOD}+O`, disabled: offline, run: () => addFolder(app) },
          { id: "rescan", group: "b", label: "Rescan library", icon: "refresh", shortcut: "F5", disabled: offline, run: () => rescanLibrary(app) },
          { id: "analysis", group: "c", label: "Background analysis", icon: "wave", disabled: offline, submenu: [
            jobs.report?.paused
              ? { id: "resume", label: "Resume", icon: "play", run: () => jobs.resume() }
              : { id: "pause", label: "Pause", icon: "pause", run: () => jobs.pause() },
            { id: "stop", label: "Stop and clear the queue", icon: "close", hint: "Pauses and forgets queued work; it is found again on the next scan", run: () => jobs.stop() },
            { id: "retry", label: "Retry failed", icon: "refresh", disabled: !jobs.report?.failed, run: () => jobs.retry() },
          ] },
        ],
      },
      {
        id: "playback",
        label: "Playback",
        items: [
          { id: "toggle", group: "a", label: p.playing ? "Pause" : "Play", icon: p.playing ? "pause" : "play", shortcut: "Space", disabled: !p.queue.length, run: () => player.toggle() },
          { id: "next", group: "a", label: "Next", icon: "next", shortcut: `${MOD}+→`, disabled: !p.queue.length, run: () => player.next({ fade: p.crossfade > 0 }) },
          { id: "prev", group: "a", label: "Previous", icon: "prev", shortcut: `${MOD}+←`, disabled: !p.queue.length, run: () => player.prev() },
          { id: "shuffle", group: "b", label: "Shuffle", checked: p.shuffle, shortcut: `${MOD}+S`, run: () => player.setShuffle(!p.shuffle) },
          { id: "repeat", group: "b", label: "Repeat", submenu: (["off", "all", "one"] as const).map((r) => ({ id: r, label: r === "off" ? "Off" : r === "all" ? "All" : "One song", checked: p.repeat === r, run: () => { while (player.get().repeat !== r) player.cycleRepeat(); } })) },
          { id: "crossfade", group: "c", label: "Crossfade", submenu: [0, 3, 6, 9, 12].map((s) => ({ id: `xf${s}`, label: s ? `${s} seconds` : "Off (gapless)", checked: p.crossfade === s, run: () => player.setCrossfade(s) })) },
          { id: "levelling", group: "c", label: "Volume levelling", checked: p.levelling, hint: "Plays every song at the same loudness, from Nova's measurements", run: () => player.setLevelling(!p.levelling) },
          { id: "mute", group: "d", label: p.muted ? "Unmute" : "Mute", icon: p.muted ? "vol" : "mute", shortcut: `${MOD}+M`, run: () => player.toggleMute() },
          { id: "clear", group: "d", label: "Clear queue", icon: "trash", disabled: !p.queue.length, run: () => player.clear() },
        ],
      },
      {
        id: "tools",
        label: "Tools",
        items: [
          { id: "flow", group: "a", label: "Flow", icon: "wave", run: () => app.navigate("flow") },
          { id: "studio", group: "a", label: "Tag editor", icon: "edit", run: () => app.navigate("studio") },
        ],
      },
      {
        id: "plugins",
        label: "Plugins",
        items: [
          { id: "manager", group: "a", label: "Plugin manager…", icon: "plug", run: () => app.navigate("plugins") },
        ],
      },
      {
        id: "help",
        label: "Help",
        items: [
          { id: "shortcuts", group: "a", label: "Keyboard shortcuts", icon: "key", shortcut: "?", run: () => app.openSettings("shortcuts") },
          { id: "syntax", group: "a", label: "Search syntax", icon: "search", run: () => app.openSettings("search") },
          { id: "plugins-guide", group: "b", label: "Writing a plugin", icon: "plug", run: () => app.openSettings("plugin-guide") },
          ...(isDesktop()
            ? [{ id: "updates", group: "c", label: "Check for updates…", icon: "download", run: () => void checkAndTell() } as MenuItem]
            : []),
          { id: "about", group: "c", label: "About Nova", icon: "info", run: () => app.openSettings("about") },
        ],
      },
    ];
    // Plugin contributions join the end of their menu.
    for (const menu of menus) {
      const extra = reg.menus[menu.id] ?? [];
      if (extra.length) menu.items = [...menu.items, ...extra.map((x) => ({ ...x, group: `z-${x.group ?? "plugin"}` }))];
    }
    return menus;
  }, [app, p, reg, jobs.report, offline, session]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function focusSearch() {
  const el = document.querySelector<HTMLInputElement>("[data-nv-search]");
  el?.focus();
  el?.select();
}
